const STORAGE_KEY = 'immo:selectedOrganizationId';
const NARROW_STORAGE_KEY = 'immo:narrowedOrganizationId';

// The active workspace is no longer tracked here — it lives in the URL
// (`/w/:workspaceId`, see `workspace-path.ts`), so links are self-describing and
// a member can never be stranded in a workspace they cannot leave (IMM-322).
// The selected/narrowed Standort below stay client-side: they are an in-app view
// preference within the active workspace, not part of a shareable link.

export function readSelectedOrganizationId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writeSelectedOrganizationId(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Quota exceeded or private-browsing — silently no-op.
  }
}

/**
 * The Standort the member has narrowed their visible lists to, or `null` for the
 * default unnarrowed "all visible" view. Distinct from the selected organisation
 * (which always resolves to a single org for create-form defaults) — the narrow
 * is the explicit list filter the Standort selector drives.
 */
export function readNarrowedOrganizationId(): string | null {
  try {
    return localStorage.getItem(NARROW_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writeNarrowedOrganizationId(id: string | null): void {
  try {
    if (id === null) {
      localStorage.removeItem(NARROW_STORAGE_KEY);
    } else {
      localStorage.setItem(NARROW_STORAGE_KEY, id);
    }
  } catch {
    // Quota exceeded or private-browsing — silently no-op.
  }
}

export function resolveSelectedOrganization<T extends { id: string; accessible?: boolean | undefined }>(
  organizations: ReadonlyArray<T>,
): T | undefined {
  // Ancestor context rows (`accessible === false`) only root the Standort tree —
  // they carry no member role, so selecting one 404s the per-org permissions
  // fetch. A partner onboarded as a subsidiary sees its parent's main org as
  // such a row; never resolve the selection to it.
  const selectable = organizations.filter((o) => o.accessible !== false);
  const saved = readSelectedOrganizationId();

  if (saved !== null) {
    const found = selectable.find((o) => o.id === saved);

    if (found !== undefined) return found;
  }

  // Saved id absent or no longer selectable — fall back to the first selectable
  // entry (the first overall only if none are flagged inaccessible).
  return selectable[0] ?? organizations[0];
}
